import {
    Box,
    Divider,
    styled,
} from '@mui/material';
import {
    ApStepper,
    ApStepperStep,
} from '@uipath/portal-shell-react';
import type { StepStatus } from '@uipath/portal-shell-types';
import React from 'react';
import { useIntl } from 'react-intl';

import { useUiStepper } from './UiStepperContextProvider';

const StepperContainer = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    borderTop: '1px solid var(--color-border-de-emp)',
    flex: 1,
    minHeight: 0,
}));

const StepperPanel = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    width: '300px',
    alignItems: 'flex-start',
    borderRight: '1px solid var(--color-border-de-emp)',
    height: '100%',
    overflow: 'auto',
}));

const StepperContent = styled(Box)(() => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'auto',
}));

const StepperFooter = styled(Box)(() => ({ padding: '24px' }));

const StepperNavigation = styled(Box)(() => ({ padding: '24px 16px 48px 24px' }));

interface StepDefinition {
    stepKey: string;
    component: React.ComponentType;
    status?: StepStatus;
}

interface UiStepperProps {
    steps: StepDefinition[];
    showStepContent?: boolean;
    children?: React.ReactNode;
    stepperVariant?: 'icon' | 'number';
    stepperFooter?: React.ReactNode;
}

export const UiStepper: React.FC<UiStepperProps> = ({
    steps,
    showStepContent = true,
    children,
    stepperVariant = 'number',
    stepperFooter,
}) => {
    const {
        currentStep,
        goToStep,
        canNavigateToStep,
        visitedSteps,
    } = useUiStepper();
    const { formatMessage: translate } = useIntl();

    const StepComponent = steps[currentStep].component;

    const handleStepClick = (event: any) => {
        const targetStep = event.detail;
        if (canNavigateToStep(targetStep)) {
            goToStep(targetStep);
        }
    };

    return (
        <StepperContainer data-cy="ui-stepper">
            <StepperPanel data-cy="ui-stepper-panel">
                <StepperNavigation>
                    <ApStepper
                        vertical
                        activeStep={currentStep}
                        stepsClickable
                        variant={stepperVariant}
                        onStepClick={handleStepClick}
                        data-cy='wizard-stepper'>
                        {steps.map((step, index) => {
                            // Determine status: use explicit status if provided, otherwise derive from visitedSteps
                            let stepStatus = step.status;
                            if (!stepStatus) {
                                if (index === currentStep) {
                                    stepStatus = 'inprogress';
                                } else if (visitedSteps.has(index)) {
                                    stepStatus = 'success';
                                } else {
                                    stepStatus = 'pending';
                                }
                            }

                            return (
                                <ApStepperStep
                                    key={index}
                                    label={translate({
                                        id: step.stepKey,
                                        defaultMessage: step.stepKey,
                                    })}
                                    disabled={!visitedSteps.has(index)}
                                    status={stepStatus} />
                            );
                        })}
                    </ApStepper>
                </StepperNavigation>
                {stepperFooter && (
                    <>
                        <Divider
                            orientation='horizontal'
                            flexItem />
                        <StepperFooter data-cy="ui-stepper-footer">
                            {stepperFooter}
                        </StepperFooter>
                    </>
                )}
            </StepperPanel>
            {showStepContent && (
                <StepperContent data-cy="ui-stepper-content">
                    <StepComponent />
                </StepperContent>
            )}
            {children}
        </StepperContainer>
    );
};
